import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { STRINGS } from '@/strings/strings';

/**
 * A-12 Save-Success-Modal — renders ONLY in `?mode=new` after a
 * successful POST /api/children. Briefing verbatim two-CTA modal:
 * primary continues to a fresh new-child form, secondary returns to
 * the children list.
 *
 * Stays route-private (single consumer today). Promote to
 * `pwa/src/components/patterns/` only when a 2nd consumer surfaces
 * the same two-CTA save-success pattern.
 */
export interface ChildSaveSuccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired when user taps "Weiteres Kind" — caller resets form to new-mode. */
  onAddAnother: () => void;
  /** Fired when user taps "Nein" — caller navigates to /account/children. */
  onDone: () => void;
}

export function ChildSaveSuccessModal({
  open,
  onOpenChange,
  onAddAnother,
  onDone,
}: ChildSaveSuccessModalProps) {
  const copy = STRINGS.account.childProfile.saveSuccessModal;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} data-testid="child-save-success-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2
              className="w-5 h-5 text-[color:var(--color-success)]"
              aria-hidden="true"
            />
            <DialogTitle>{copy.headline}</DialogTitle>
          </div>
          <DialogDescription>{copy.body}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="m-0 flex flex-col-reverse gap-2 border-0 bg-transparent p-0 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onDone();
            }}
          >
            {copy.secondaryCta}
          </Button>
          <Button
            type="button"
            onClick={() => {
              onOpenChange(false);
              onAddAnother();
            }}
          >
            {copy.primaryCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
